import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AuthentifiedPage from "./AuthentifiedPage";
import CollecteSuccessMessage from "../components/CollecteSuccessMessage";
import CollecteErrorMessage from "../components/CollecteErrorMessage";
import AddDocumentMenuWrapper from "../components/AddDocumentMenuWrapper";
import { stringToIcon } from "../components/AddDocumentMenuWrapper";
import "../styles/ListFormAdmin.css";
import "../styles/CalendarPage.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { Domaine, SousDomaine, SousSousDomaine } from "../types/Domaine";
import { MenuService } from "../services/MenuService";
import { useAuth } from "../context/AuthContext";
import { FenetreService } from "../services/FenetreService";
import { SousFenetreService } from "../services/SousFenetreService";

type Status = "active" | "inactive";

interface NotificationState {
  visible: boolean;
  message: string;
}

interface ToggleButtonProps {
  status: Status;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}

// Bouton toggle simple
const ToggleButton: React.FC<ToggleButtonProps> = ({ status, onClick }) => (
  <button
    onClick={onClick}
    style={{
      width: 30,
      height: 18,
      borderRadius: 9,
      border: "1px solid #ccc",
      backgroundColor: status === "active" ? "#46b737ff" : "#cc2f2fff",
      display: "flex",
      alignItems: "center",
      justifyContent: status === "active" ? "flex-end" : "flex-start",
      padding: 2,
      cursor: "pointer",
      transition: "all 0.3s",
    }}
    onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
    onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
  >
    <div
      style={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: "#fff",
        transition: "all 0.3s",
      }}
    />
  </button>
);

const GestionMenuPage: React.FC = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [menus, setMenus] = useState<Domaine[]>([]);
  const [openMenus, setOpenMenus] = useState<Set<number>>(new Set());
  const [openFenetres, setOpenFenetres] = useState<Set<number>>(new Set());
  const [showMenu, setShowMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState<NotificationState>({ visible: false, message: "" });
  const [error, setError] = useState<NotificationState>({ visible: false, message: "" });

  const [page, setPage] = useState(1);
  const itemsPerPage = 5;
  const total = menus.length;

  const totalPages = Math.ceil(total / itemsPerPage);
  const indexOfLastItem = page * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const pageData = menus.slice(indexOfFirstItem, indexOfLastItem);

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);


  // Notifications
  const showNotification = useCallback((type: "success" | "error", message: string) => {
    const setter = type === "success" ? setSuccess : setError;
    setter({ visible: true, message });
    setTimeout(() => setter({ visible: false, message: "" }), 3000);
  }, []);

 const handleCreate = useCallback(async () => {
  setShowMenu(false);
  setLoading(true);

  await fetchMenus();

  setLoading(false);
  showNotification("success", "Création effectuée avec succès");
}, []);

  const handleCreateIndicator = (domain: Domaine, sousDomain?: SousDomaine, sousSousDomain?: SousSousDomaine) => {
    domain.icon = undefined;
    setShowMenu(false);
    setLoading(true);

    setTimeout(() => {
      setLoading(false);

      navigate("/create-indicateur", {
        state: {
          tableName: domain.tbl_name,
          sousDomain,
          sousSousDomain,
          mode: "createIndicator",
        },
      });
    }, 500);
  };
      const handleCreateRapport = (
      domain: Domaine,
      sousDomain?: SousDomaine,
      sousSousDomain?: SousSousDomaine
    ) => {
      domain.icon = undefined;
      setShowMenu(false);
      setLoading(true);
  
      setTimeout(() => {
        setLoading(false);
  
        navigate("/create-rapport", {
          state: {
            domain,
            sousDomain,
            sousSousDomain,
            mode: "createRapport",
          },
        });
      }, 500);
    };

  const fetchMenus = async () => {
    setLoading(true);
    try {
      const data = await MenuService.getAllMenuDetails(api);
      setMenus(data);
    } catch (err: any) {
      console.error(err);
      showNotification("error", "Erreur lors du chargement des menus");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenus();
  }, [api, showNotification]);

  const toggleStatus = useCallback(
    async (targetId: number, type: "menu" | "fenetre" | "sousFenetre") => {

      try {
        const updatedMenus: Domaine[] = await Promise.all(
          menus.map(async (menu) => {

            /* ------------------------- MENU ------------------------- */
            if (type === "menu" && menu.id === targetId) {

              if (menu.sousDomaines?.some((f) => f.sIsActive)) {
                showNotification(
                  "error",
                  "Impossible de désactiver ce menu tant qu’un sous-domaine est actif !"
                );
                return menu;
              }

              const newActive = !menu.mIsActive;

              if (newActive) await MenuService.enableMenu(api, menu.id!);
              else await MenuService.disableMenu(api, menu.id!);

              showNotification(
                "success",
                `Menu "${menu.label}" ${newActive ? "activé" : "désactivé"}`
              );

              return { ...menu, m_is_active: newActive };
            }

            /* ------------------------- FENÊTRE ------------------------- */
            if (menu.sousDomaines && type === "fenetre") {
              return {
                ...menu,
                sousDomaines: await Promise.all(
                  menu.sousDomaines.map(async (fenetre) => {
                    if (fenetre.id === targetId) {

                      if (fenetre.sousSousDomaines?.some((s) => s.sfIsActive)) {
                        showNotification(
                          "error",
                          "Impossible de désactiver cette fenêtre tant qu’une sous-fenêtre est active !"
                        );
                        return fenetre;
                      }

                      const newActive = !fenetre.sIsActive;

                      if (newActive) await FenetreService.enableFenetre(api, fenetre.id!);
                      else await FenetreService.disableFenetre(api, fenetre.id!);

                      showNotification(
                        "success",
                        `Fenêtre "${fenetre.label}" ${newActive ? "activée" : "désactivée"}`
                      );

                      return { ...fenetre, f_is_active: newActive };
                    }

                    return fenetre;
                  })
                ),
              };
            }

            /* ------------------------- SOUS-FENÊTRE ------------------------- */
            if (menu.sousDomaines && type === "sousFenetre") {
              return {
                ...menu,
                sousDomaines: await Promise.all(
                  menu.sousDomaines.map(async (fenetre) => {
                    if (fenetre.sousSousDomaines) {
                      return {
                        ...fenetre,
                        sousSousDomaines: await Promise.all(
                          fenetre.sousSousDomaines.map(async (sous) => {
                            if (sous.id === targetId) {

                              const newActive = !sous.sfIsActive;

                              if (newActive)
                                await SousFenetreService.enableSousFenetre(api, sous.id!);
                              else
                                await SousFenetreService.disableSousFenetre(api, sous.id!);

                              showNotification(
                                "success",
                                `Sous-Fenêtre "${sous.label}" ${newActive ? "activée" : "désactivée"}`
                              );

                              return { ...sous, sf_is_active: newActive };
                            }

                            return sous;
                          })
                        ),
                      };
                    }

                    return fenetre;
                  })
                ),
              };
            }

            return menu;
          })
        );

        setMenus(updatedMenus);
        await fetchMenus();
      } catch (err: any) {
        console.error(err);
        showNotification("error", "Erreur lors de la modification du statut");
      }
    },
    [menus, api, showNotification]
  );

  const toggleOpen = useCallback(
    (id: number, setter: React.Dispatch<React.SetStateAction<Set<number>>>) => {
      setter((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        return newSet;
      });
    },
    []
  );

  const Arrow: React.FC<{ isOpen: boolean }> = ({ isOpen }) => (
    <span
      style={{
        display: "inline-block",
        width: 20,
        color: "#000",
        fontWeight: "bold",
        transition: "transform 0.2s ease",
        transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
      }}
    >
      ▼
    </span>
  );

  // Sous-Fenêtre
  const SousSousDomaineItem: React.FC<{ sous: SousSousDomaine }> = ({ sous }) => (
    <div className="d-flex justify-content-between align-items-center p-1 rounded" style={{ backgroundColor: "#ffffffff" }}>
      <span>{sous.label}</span>
      <ToggleButton
        status={sous.sfIsActive ? "active" : "inactive"}
        onClick={() => toggleStatus(sous.id!, "sousFenetre")}
      />
    </div>
  );

  // Fenêtre
  const SousDomaineItem: React.FC<{ fenetre: SousDomaine }> = ({ fenetre }) => {
    const isOpen = openFenetres.has(fenetre.id!);
    const hasSous = fenetre.sousSousDomaines?.length! > 0;

    return (
      <div className="mt-2" style={{ paddingLeft: 16 }}>
        <div
          className="d-flex justify-content-between align-items-center p-2 rounded"
          style={{ cursor: hasSous ? "pointer" : "default", backgroundColor: "#f1f8e9" }}
          onClick={() => hasSous && toggleOpen(fenetre.id!, setOpenFenetres)}
        >
          <div className="d-flex align-items-center">
            {hasSous && <Arrow isOpen={isOpen} />}
            <span className="ms-1">{fenetre.label}</span>
          </div>
          <ToggleButton
            status={fenetre.sIsActive ? "active" : "inactive"}
            onClick={(e) => {
              e.stopPropagation();
              toggleStatus(fenetre.id!, "fenetre");
            }}
          />
        </div>

        {isOpen && fenetre.sousSousDomaines?.map((sous) => (
          <div key={sous.id} className="mt-1 ms-3">
            <SousSousDomaineItem sous={sous} />
          </div>
        ))}
      </div>
    );
  };

  // Menu principal
  const MenuItem: React.FC<{ menu: Domaine }> = ({ menu }) => {
    const isOpen = openMenus.has(menu.id!);
    const hasSous = menu.sousDomaines?.length! > 0;

    return (
      <div className="accordion-item mb-3 border rounded shadow-sm">
        <div
          className="d-flex justify-content-between align-items-center p-3 rounded"
          style={{ cursor: hasSous ? "pointer" : "default", backgroundColor: "#e0f7fa" }}
          onClick={() => hasSous && toggleOpen(menu.id!, setOpenMenus)}
        >
          <div className="d-flex align-items-center">
            {hasSous && <Arrow isOpen={isOpen} />}
            {stringToIcon(menu.iconName, 22, "mx-2")}
            <span className="fw-semibold">{menu.label}</span>
          </div>
          <ToggleButton status={menu.mIsActive ? "active" : "inactive"} onClick={(e) => { e.stopPropagation(); toggleStatus(menu.id!, "menu"); }} />
        </div>

        {isOpen && menu.sousDomaines?.map((fenetre) => <SousDomaineItem key={fenetre.id} fenetre={fenetre} />)}
      </div>
    );
  };

  return (
    <AuthentifiedPage>
      <main className="container py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="text-success fw-bold mb-0">Gestion des Menu</h4>
          <button
            className="btn-add-event d-flex align-items-center gap-2"
            onClick={() => setShowMenu(true)}
            disabled={loading}
          >
            <i className="bi bi-plus-lg"></i>
            <span>Créer un menu</span>
          </button>
        </div>

        <CollecteSuccessMessage visible={success.visible} message={success.message} />
        <CollecteErrorMessage visible={error.visible} message={error.message} />

        {showMenu && (
          <AddDocumentMenuWrapper
            onSelect={() => {}}
            onCreateForm={handleCreate}
            onCreateIndicator={handleCreateIndicator}
            onCreateRapport={handleCreateRapport}
            onClose={async () => {
              setShowMenu(false);
              await fetchMenus();
            }}
          />
        )}

        {loading ? (
          <div className="text-center my-5">
            <div className="spinner-border text-primary" role="status" />
            <p className="mt-3 text-muted">Chargement des données...</p>
          </div>
        ) : (
          <div className="accordion mt-4">

            {menus.length === 0 ? (
              <div className="alert alert-info text-center">
                <i className="bi bi-info-circle me-2"></i>
                Aucun menu disponible. Créez-en un pour commencer.
              </div>
            ) : (
              <>
                {/* Données paginées */}
                {pageData.map((menu) => (
                  <MenuItem key={menu.id} menu={menu} />
                ))}

                {/* Pagination */}
                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                  <small className="text-muted">
                    {pageData.length} menus affichés sur {total}
                  </small>

                  <nav>
                    <ul className="pagination pagination-sm mb-0">

                      {/* Précédent */}
                      <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => setPage(page - 1)}>
                          <i className="bi bi-chevron-left"></i>
                        </button>
                      </li>

                      {/* Numéros */}
                      {pageNumbers.map((num) => (
                        <li
                          key={num}
                          className={`page-item ${num === page ? "active" : ""}`}
                        >
                          <button className="page-link" onClick={() => setPage(num)}>
                            {num}
                          </button>
                        </li>
                      ))}

                      {/* Suivant */}
                      <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => setPage(page + 1)}>
                          <i className="bi bi-chevron-right"></i>
                        </button>
                      </li>

                    </ul>
                  </nav>
                </div>
              </>
            )}
          </div>
        )}

      </main>
    </AuthentifiedPage>
  );
};

export default GestionMenuPage;